import { useEffect, useMemo, useState } from 'react';
import { Moment } from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    convertToWalkingSessions,
    fetchWalkingSessionsThunk,
    NewBookingsStatus,
    WalkingSessionDto
} from '@spike/new-bookings-action';
import { WalkingSession } from '@spike/new-booking-model';
import { useApiClientWrapper } from 'hooks';

export interface BookingSessions {
    sessions: Array<WalkingSession>;
    loading: boolean;
}

export const useBookingSessions = (
    serviceId: number | undefined,
    date: Moment | undefined,
    enabled: boolean,
    appointmentId?: number
): BookingSessions => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const sessions = useSelector<RootState, Array<WalkingSession>>(state => state.newBookings.walkingSessions);
    const status = useSelector<RootState, NewBookingsStatus>(state => state.newBookings.status);

    const targetDate = date?.isValid() ? date.format('YYYY-MM-DD') : undefined;
    const active = enabled && Boolean(serviceId) && Boolean(targetDate);

    useEffect(() => {
        if (!active) return;

        dispatch(fetchWalkingSessionsThunk(apiClientWrapper, serviceId!, date!, appointmentId));
        // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClientWrapper is unstable by design
    }, [active, serviceId, targetDate, appointmentId, dispatch]);

    return {
        sessions: active ? sessions : [],
        loading: active && status === NewBookingsStatus.FetchingWalkingSessions
    };
};

const SESSIONS_URL = 'service_sessions/for_booking';
const DAY_FORMAT = 'YYYY-MM-DD';

export interface BookingSessionsByDay {
    // Keyed by `YYYY-MM-DD`; a day is absent until its sessions arrive.
    sessionsByDay: Record<string, Array<WalkingSession>>;
    loadingDays: Array<string>;
}

// Several selected dates: the store keeps a single day's list, so each day is fetched on its
// own and kept here. Days already fetched for the same service are not requested again.
export const useBookingSessionsByDay = (
    serviceId: number | undefined,
    dates: Array<Moment>,
    enabled: boolean,
    appointmentId?: number
): BookingSessionsByDay => {
    const apiClientWrapper = useApiClientWrapper();
    const [cache, setCache] = useState<{ key: string; days: Record<string, Array<WalkingSession>> }>({
        key: '',
        days: {}
    });

    const cacheKey = `${serviceId ?? ''}|${appointmentId ?? ''}`;
    const days = useMemo(
        () => Array.from(new Set(dates.filter(date => date.isValid()).map(date => date.format(DAY_FORMAT)))).sort(),
        [dates]
    );
    const active = enabled && Boolean(serviceId) && days.length > 0;
    const cachedDays = cache.key === cacheKey ? cache.days : {};
    const missingDays = active ? days.filter(day => cachedDays[day] === undefined) : [];
    const missingKey = missingDays.join(',');

    useEffect(() => {
        if (!active || missingDays.length === 0) return;

        let cancelled = false;
        const appointmentParam = appointmentId ? `&appointment_id=${appointmentId}` : '';

        Promise.all(
            missingDays.map(day =>
                apiClientWrapper.apiClient
                    .get<{ sessions?: Array<WalkingSessionDto> }>(
                        `${SESSIONS_URL}?marketplace_id=${apiClientWrapper.marketplaceId}&service_id=${serviceId}&date=${day}${appointmentParam}`,
                        apiClientWrapper.requestConfig
                    )
                    .then(response => [day, convertToWalkingSessions(response.data.sessions ?? [])] as const)
                    // A failed day shows as having no sessions, which still lets the user book it.
                    .catch(() => [day, [] as Array<WalkingSession>] as const)
            )
        ).then(results => {
            if (cancelled) return;

            setCache(prev => {
                const base = prev.key === cacheKey ? prev.days : {};

                return { key: cacheKey, days: { ...base, ...Object.fromEntries(results) } };
            });
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- apiClientWrapper is unstable by design
    }, [active, cacheKey, missingKey]);

    return {
        sessionsByDay: active ? cachedDays : {},
        loadingDays: missingDays
    };
};

export default useBookingSessions;
